import { useEffect, useLayoutEffect, useState, useRef, lazy, Suspense } from 'react';
import { flushSync } from 'react-dom';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { AuthProvider, useAuth } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { VegModeProvider } from './context/VegModeContext';
import { Toaster } from './components/ui/sonner';
import BottomNav from './components/BottomNav';
import CaAgentChat from './components/CaAgentChat';
import CartDrawer from './components/CartDrawer';
import AppDownloadBanner from './components/AppDownloadBanner';
import usePushNotifications from './hooks/usePushNotifications';
import useAppSessionPing from './hooks/useAppSessionPing';
import useNativeShellInit from './hooks/useNativeShellInit';
import useIosEdgeSwipeBack from './hooks/useIosEdgeSwipeBack';
import { TAB_ROOTS, STAFF_TAB_ROOTS } from './utils/navConstants';
import { trackAppOpen, startSessionHeartbeat } from './utils/analytics';
import { initClarity } from './utils/clarity';
import { takeStaffRoute } from './utils/staffSession';
import { useFeatureFlags } from './hooks/useFeatureFlags';
import { applySeasonalTheme, rememberSeasonalTheme } from './utils/seasonalTheme';
import {
  MenuSkeleton,
  OffersSkeleton,
  PlansSkeleton,
  CartSkeleton,
  CheckoutSkeleton,
  ProductDetailSkeleton,
} from './components/PageSkeletons';

// Only Landing loaded eagerly (homepage / LCP-critical)
import Landing from './pages/Landing';

/** Retry a dynamic import once, then force-reload if the chunk is missing (stale deploy). */
// Preload-aware lazy with retry-then-reload. Beyond code-splitting, this exposes
// a `.preload()` and — critically — renders the module SYNCHRONOUSLY once it has
// resolved. Plain `React.lazy` suspends for one render tick on the FIRST render of
// a component even when its chunk is already cached (its internal status only flips
// to "resolved" on that first render), and our native View Transition snapshots
// that one-tick Suspense fallback (the page skeleton) and animates it for the whole
// ~340ms transition → the "flicker" on the first switch to each tab. Rendering the
// already-resolved module directly means there's no fallback for the VT to capture.
function lazyWithReload(importFn) {
  let promise = null;
  let Resolved = null;
  const load = () => {
    if (!promise) {
      promise = importFn()
        .catch(() => importFn())
        .catch(() => { window.location.reload(); return new Promise(() => {}); })
        .then((m) => { Resolved = m.default || m; return m; });
    }
    return promise;
  };
  const Lazy = lazy(load);
  const Wrapped = (props) => {
    if (Resolved) {
      const C = Resolved;
      return <C {...props} />;   // synchronous — no suspend-once, nothing for the VT to snapshot
    }
    return <Lazy {...props} />;  // genuinely-cold path (navigated before preload) still suspends
  };
  Wrapped.preload = load;
  return Wrapped;
}

// All other routes code-split — loaded on demand
const Login = lazyWithReload(() => import('./pages/Login'));
const Menu = lazyWithReload(() => import('./pages/Menu'));
const Cart = lazyWithReload(() => import('./pages/Cart'));
const Checkout = lazyWithReload(() => import('./pages/Checkout'));
const OrderTracking = lazyWithReload(() => import('./pages/OrderTracking'));
const HelpWithOrder = lazyWithReload(() => import('./pages/HelpWithOrder'));
const Profile = lazyWithReload(() => import('./pages/Profile'));
const Settings = lazyWithReload(() => import('./pages/Settings'));
const Orders = lazyWithReload(() => import('./pages/Orders'));
const Favorites = lazyWithReload(() => import('./pages/Favorites'));
const AddressBook = lazyWithReload(() => import('./pages/AddressBook'));
const Coupons = lazyWithReload(() => import('./pages/Coupons'));
const AdminDashboard = lazyWithReload(() => import('./pages/AdminDashboard'));
const OfferOfTheDay = lazyWithReload(() => import('./pages/OfferOfTheDay'));
const VisitFeedback = lazyWithReload(() => import('./pages/VisitFeedback'));
const AboutUs = lazyWithReload(() => import('./pages/AboutUs'));
const Subscriptions = lazyWithReload(() => import('./pages/Subscriptions'));
const PassDetail = lazyWithReload(() => import('./pages/PassDetail'));
const PassConfirmation = lazyWithReload(() => import('./pages/PassConfirmation'));
const MySubscriptions = lazyWithReload(() => import('./pages/MySubscriptions'));
const ProductDetail = lazyWithReload(() => import('./pages/ProductDetail'));
// ProductDetailSheet is the in-app rendering of ProductDetail (vaul Drawer over
// Menu). Loaded lazily but separately so the sheet route's chunk can be
// fetched on first menu-item tap without dragging in the full-page deep-link
// variant. See App.js routing block below for the background-location pattern.
const ProductDetailSheet = lazyWithReload(() => import('./components/ProductDetailSheet'));
const ContactUs = lazyWithReload(() => import('./pages/ContactUs'));
const Terms = lazyWithReload(() => import('./pages/Terms'));
const Privacy = lazyWithReload(() => import('./pages/Privacy'));
const AccountDeletion = lazyWithReload(() => import('./pages/AccountDeletion'));
const RefundsAndCancellations = lazyWithReload(() => import('./pages/RefundsAndCancellations'));
const OrderConfirmation = lazyWithReload(() => import('./pages/OrderConfirmation'));
const Receipt = lazyWithReload(() => import('./pages/Receipt'));
const KioskLogin = lazyWithReload(() => import('./pages/KioskLogin'));
// Staff Operations Ecosystem (Business Rule #41) — reached by direct URL only,
// never linked from customer navigation. StaffLayout self-gates via /staff/me.
const StaffLayout = lazyWithReload(() => import('./pages/staff/StaffLayout'));
const StaffHome = lazyWithReload(() => import('./pages/staff/StaffHome'));
const StaffChecklists = lazyWithReload(() => import('./pages/staff/StaffChecklists'));
const StaffChecklistRun = lazyWithReload(() => import('./pages/staff/StaffChecklistRun'));
const StaffReviewQueue = lazyWithReload(() => import('./pages/staff/StaffReviewQueue'));
const StaffSops = lazyWithReload(() => import('./pages/staff/StaffSops'));
const StaffSopDetail = lazyWithReload(() => import('./pages/staff/StaffSopDetail'));
const StaffLaunches = lazyWithReload(() => import('./pages/staff/StaffLaunches'));
const StaffLaunchSeries = lazyWithReload(() => import('./pages/staff/StaffLaunchSeries'));
const StaffSeriesTest = lazyWithReload(() => import('./pages/staff/StaffSeriesTest'));
const StaffExams = lazyWithReload(() => import('./pages/staff/StaffExams'));
const StaffExamTake = lazyWithReload(() => import('./pages/staff/StaffExamTake'));
const StaffIncidents = lazyWithReload(() => import('./pages/staff/StaffIncidents'));
const StaffRecipeDetail = lazyWithReload(() => import('./pages/staff/StaffRecipeDetail'));
const StaffPlanner = lazyWithReload(() => import('./pages/staff/StaffPlanner'));
const StaffDocuments = lazyWithReload(() => import('./pages/staff/StaffDocuments'));
const StaffCupCounts = lazyWithReload(() => import('./pages/staff/StaffCupCounts'));
// Warehouse stock count (Business Rule #65) — the ticked staff member counts the Central Warehouse.
const StaffWarehouseCount = lazyWithReload(() => import('./pages/staff/StaffWarehouseCount'));
const KioskScanner = lazyWithReload(() => import('./pages/KioskScanner'));
// Investor read-only Sales access (Business Rule #56) — the investor's only screen.
const InvestorDashboard = lazyWithReload(() => import('./pages/InvestorDashboard'));
const NotFound = lazyWithReload(() => import('./pages/NotFound'));
import './App.css';

// Per-history-entry scroll offsets, keyed by RR location.key, for native
// back/forward scroll restoration (iOS restores prior scroll on back; a fresh
// push lands at the top). Module-scoped so it survives AppRoutes re-mounts.
const pbScrollPositions = new Map();

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen native:min-h-[calc(100dvh_-_env(safe-area-inset-bottom))]">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }

  return user ? children : <Navigate to="/login" replace />;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen native:min-h-[calc(100dvh_-_env(safe-area-inset-bottom))]">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }

  return user?.is_admin ? children : <Navigate to="/" replace />;
};

/** True for an active investor account (Business Rule #56). */
const isActiveInvestor = (user) => user?.staff_role === 'investor' && !!user?.staff_active;

// The investor's Sales page — an active investor, or an admin previewing it.
// Everyone else lands on the home screen. The server enforces the same rule
// (`get_sales_viewer`); this only keeps the page from rendering empty.
const InvestorRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen native:min-h-[calc(100dvh_-_env(safe-area-inset-bottom))]">
      <div className="text-lg font-medium">Loading...</div>
    </div>;
  }

  return user?.is_admin || isActiveInvestor(user) ? children : <Navigate to="/" replace />;
};

// Routes that hide the bottom nav. Checked against the BACKGROUND page when a
// ProductDetail sheet is open so the nav stays mounted under the drawer.
const HIDE_NAV_PATHS = ['/login', '/signup', '/admin', '/investor', '/checkout', '/order-confirmed', '/pass-confirmed', '/menu/item', '/receipt', '/kiosk', '/passes/', '/staff', '/f/'];

function AppRoutes() {
  // Background-location routing pattern (React Router v6 idiom). When an
  // in-app caller navigates to `/menu/item/:itemId` with
  // `state: { backgroundLocation: location }`, we render the previous
  // location's route tree (Menu) in the primary <Routes> and stack a
  // ProductDetailSheet on top via a secondary <Routes>. Menu stays mounted →
  // its scroll position is preserved across open/close of the item detail.
  //
  // Cold-open (share link / refresh / push notification) has no
  // backgroundLocation, so the primary <Routes> renders the full-page
  // `<ProductDetail />` directly. Both surfaces share the same component
  // (mode="page" vs mode="sheet"); see pages/ProductDetail.js and
  // components/ProductDetailSheet.jsx.
  const location = useLocation();
  const navigate = useNavigate();
  const backgroundLocation = location.state?.backgroundLocation;

  // Staff session continuity (Business Rule #41). Android reaps the app while
  // the system camera holds the foreground, and Capacitor cold-boots the
  // WebView at `/` — dumping a staff member mid-checklist onto the CUSTOMER
  // home screen, which carries no link back to the portal. A stored staff
  // route means the shell was killed rather than left (StaffLayout clears it
  // on a deliberate exit), so restore it. `replace` keeps Back out of the loop.
  const staffRestoreRef = useRef(false);
  useEffect(() => {
    if (staffRestoreRef.current) return;
    staffRestoreRef.current = true;
    if (location.pathname !== '/') return;
    let hasSession = false;
    try { hasSession = !!localStorage.getItem('token'); } catch { /* no-op */ }
    if (!hasSession) return;
    const path = takeStaffRoute();
    if (path) navigate(path, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Investor home (Business Rule #56): the Sales page is an investor's only
  // screen, and the native app always cold-boots at `/`. Once per launch, an
  // active investor who lands on `/` is sent there — once, so a deliberate
  // trip back to the customer home afterwards isn't bounced.
  const { user: authUser } = useAuth();
  const investorHomeRef = useRef(false);
  useEffect(() => {
    if (investorHomeRef.current || !isActiveInvestor(authUser)) return;
    investorHomeRef.current = true;
    if (location.pathname === '/') navigate('/investor', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser]);
  // Pin the rendered page to the PINNED location (backgroundLocation || location)
  // — NOT the raw URL. When ProductDetailSheet opens over Menu, location.pathname
  // flips to `/menu/item/:id` but the primary tree stays Menu, so no page change.
  const realTransitionLocation = backgroundLocation || location;
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  const isNativeShell = !!cap?.isNativePlatform?.();

  // Native page transitions via the View Transitions API (docs/NATIVE_POLISH_PLAN.md
  // Phase 4). The browser snapshots the old + new views into UA-managed pseudo-
  // elements and cross-fades/slides THOSE — the live DOM is never transformed, so
  // position:sticky headers survive (constraint a), and old+new are both present so
  // there's never a blank frame (constraint b). We make this work in declarative
  // (BrowserRouter) mode by LAGGING the rendered location: keep showing the old
  // page until startViewTransition has captured it, then commit the new page
  // synchronously (flushSync) inside the transition callback. Feature-detected;
  // falls back to an instant swap on old WebViews. Web PWA is untouched — it keeps
  // the framer-motion fade below and never calls startViewTransition.
  const vtSupported = typeof document !== 'undefined' && typeof document.startViewTransition === 'function';
  const useViewTransitions = isNativeShell && vtSupported;
  const [displayedLocation, setDisplayedLocation] = useState(realTransitionLocation);
  // Scroll offset the next page-change layout effect should land on: 0 for a
  // fresh push, the destination's saved offset for a back/forward pop. Written by
  // the transition effect, read by the scroll layout effect.
  const pendingScrollYRef = useRef(0);

  // Native only: own scroll restoration entirely. Default `'auto'` makes the
  // browser re-apply ITS remembered offset a frame after our manual restore on a
  // back/pop — the two fight and the scroll visibly bounces (e.g. Profile→back
  // restored Home to 2050, then the browser yanked it to its own ~647 and back).
  // `'manual'` stops the browser from touching scroll so our save/restore is the
  // only setter. Web keeps `'auto'` (it has no manual restore logic).
  useEffect(() => {
    if (!isNativeShell || !('scrollRestoration' in window.history)) return undefined;
    const prev = window.history.scrollRestoration;
    window.history.scrollRestoration = 'manual';
    return () => { window.history.scrollRestoration = prev; };
  }, [isNativeShell]);

  useEffect(() => {
    // Web renders realTransitionLocation directly (routedLocation below), so the
    // lagged displayedLocation is native-only — never touch it off native.
    if (!useViewTransitions) return;
    if (realTransitionLocation === displayedLocation) return;
    // Same pinned page (sheet open/close keeps the pathname) → sync, no animation.
    if (realTransitionLocation.pathname === displayedLocation.pathname) {
      setDisplayedLocation(realTransitionLocation);
      return;
    }
    // Track RR v7's monotonic history.state.idx so we know the direction even on
    // an instant swap (lower idx than last time = went back).
    const root = document.documentElement;
    const idx = window.history.state?.idx;
    const prevIdx = root.dataset.pbNavIdx;
    const isBack = idx != null && prevIdx !== '' && prevIdx != null ? Number(idx) < Number(prevIdx) : false;
    root.dataset.pbNavIdx = idx != null ? String(idx) : '';
    // Scroll restoration: save the OUTGOING page's scroll keyed by its history
    // entry, then on a back/forward POP restore the DESTINATION's saved offset
    // (read by the scroll layout effect) instead of resetting to the top — so
    // returning to a page lands where you left it, the native iOS behaviour. A
    // fresh push keeps the reset-to-top (savedY undefined → 0).
    const scroller = document.scrollingElement || root;
    if (displayedLocation.key) pbScrollPositions.set(displayedLocation.key, scroller.scrollTop);
    const savedY = isBack && realTransitionLocation.key != null
      ? pbScrollPositions.get(realTransitionLocation.key)
      : undefined;
    pendingScrollYRef.current = savedY != null ? savedY : 0;
    // Tab↔tab (both bottom-nav roots) is an INSTANT swap — no view transition. The
    // cross-fade there was imperceptible AND it snapshot image-heavy pages
    // (Home/Menu) mid-load, so their <img>s flashed as the old snapshot cross-faded
    // over still-decoding new images. Only hierarchical push/pop animates (the slide).
    // Staff-portal tab taps (Business Rule #41) are tab switches too — a
    // vt-slide push between bottom-tab roots reads as wrong-direction drilling.
    const isTabSwitch =
      (TAB_ROOTS.has(displayedLocation.pathname) && TAB_ROOTS.has(realTransitionLocation.pathname)) ||
      (STAFF_TAB_ROOTS.has(displayedLocation.pathname) && STAFF_TAB_ROOTS.has(realTransitionLocation.pathname));
    if (isTabSwitch) {
      root.classList.remove('vt-slide', 'vt-back');
      // White-flash fix for a deep scroll offset + a SHORTER destination (e.g. a
      // long-scrolled Menu → Offers/Plans, which arrive in a one-screen loading
      // state). Two parts:
      //
      // 1) HARD-CUT View Transition (`vt-tab` → `animation: none` in index.css):
      //    the UA composites static snapshots across the swap. A hard cut, NOT the
      //    UA cross-fade, because cross-fading image-heavy Home/Menu flashed their
      //    still-decoding <img>s (the original reason tab switches skipped VT).
      //
      // 2) Pin the document height across the swap. The VT mask alone wasn't
      //    enough: JS resets scrollTop to 0 synchronously, but WKWebView's scroll
      //    view runs on a separate thread, and when the document shrinks (tall
      //    source → short destination) it has to clamp its stale deep offset down
      //    to the top — a live-layer clamp the snapshot doesn't cover and that no
      //    amount of mask-holding hides (verified on-device: logical scrollTop is
      //    already 0 at every checkpoint; the flash is purely the scroll-view
      //    clamp). So we remove the clamp: pin `body` to the current (tall) height
      //    so the scroll view never goes out of bounds, let the scroll reset to 0
      //    (valid on a tall page), and release the pin only after the transition
      //    finishes — by then scroll has settled at 0, so the shrink is harmless.
      //    A tall destination (Cart) never needed this; only short ones flashed.
      //    We pin `body`, never the scroller's own scrollTop, because touching the
      //    outgoing page's scroll desynced every transition when tried.
      const se = document.scrollingElement || document.documentElement;
      document.body.style.minHeight = `${se.scrollHeight}px`;
      root.classList.add('vt-tab');
      const cleanup = () => {
        root.classList.remove('vt-tab');
        document.body.style.minHeight = '';
      };
      const vt = document.startViewTransition(() => {
        flushSync(() => setDisplayedLocation(realTransitionLocation));
      });
      // Always release the pin + class, even if the transition is interrupted by
      // a rapid second nav (finished rejects) — otherwise body stays stuck tall.
      if (vt?.finished) vt.finished.then(cleanup, cleanup);
      else cleanup();
      return;
    }
    root.classList.add('vt-slide');
    root.classList.toggle('vt-back', isBack);
    const vt = document.startViewTransition(() => {
      flushSync(() => setDisplayedLocation(realTransitionLocation));
    });
    // Swallow the rejection a skipped/interrupted transition produces (rapid nav).
    vt.finished?.catch(() => {});
  }, [realTransitionLocation, displayedLocation, useViewTransitions]);

  // On web (no view transitions) render the live pinned location so AnimatePresence
  // behaves exactly as before; on native render the lagged location the transition
  // commits. routedLocation is the single source the <Routes> render against.
  const routedLocation = useViewTransitions ? displayedLocation : realTransitionLocation;

  // Native: reposition scroll when the DISPLAYED page changes (in sync with the
  // transition commit). Keyed off the pinned/displayed pathname so opening/closing
  // the ProductDetail sheet does NOT reset Menu's preserved scroll.
  useLayoutEffect(() => {
    if (!isNativeShell) return;
    // Forced to `auto` so the global `html{scroll-behavior:smooth}` doesn't animate
    // the reposition — a smooth animate from a stale offset read as the destination
    // "also scrolling" (Home→Cart) and flashed past short content (R2/R7 fix). The
    // target is 0 for a fresh push (top), or the destination's saved offset on a
    // back/forward pop (pendingScrollYRef, set in the transition effect) so a page
    // restores to where you left it instead of jumping to the top. Capped to the
    // current max scroll so restoring a stale deep offset onto a now-shorter page
    // can't land out of bounds.
    const el = document.scrollingElement || document.documentElement;
    const maxY = Math.max(0, el.scrollHeight - window.innerHeight);
    const targetY = Math.min(Math.max(0, pendingScrollYRef.current || 0), maxY);
    const prevBehavior = el.style.scrollBehavior;
    el.style.scrollBehavior = 'auto';
    window.scrollTo(0, targetY);
    el.scrollTop = targetY;
    el.style.scrollBehavior = prevBehavior;
  }, [routedLocation.pathname, isNativeShell]);

  // The routed pages. On native we render them BARE — the View Transitions API
  // owns the animation (no AnimatePresence, whose mode="wait" gap was the old
  // white flash). Web keeps the framer-motion fade-up below.
  const pageRoutes = (
    <Routes location={routedLocation}>
            <Route path="/" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            {/* Unified auth (Business Rule #7): Login now creates the account + captures
                consent inline, so /signup just redirects. Kept so old links / app-store
                metadata / deep links don't 404. */}
            <Route path="/signup" element={<Navigate to="/login" replace />} />
            <Route path="/menu" element={<Suspense fallback={<MenuSkeleton />}><Menu /></Suspense>} />
            <Route path="/menu/item/:itemId" element={<Suspense fallback={<ProductDetailSkeleton />}><ProductDetail /></Suspense>} />
            <Route path="/cart" element={<Suspense fallback={<CartSkeleton />}><Cart /></Suspense>} />
            <Route path="/checkout" element={<Suspense fallback={<CheckoutSkeleton />}><Checkout /></Suspense>} />
            <Route path="/order-confirmed/:orderId" element={<OrderConfirmation />} />
            <Route path="/receipt/:orderId" element={<Receipt />} />
            <Route path="/orders/:orderId" element={<PrivateRoute><OrderTracking /></PrivateRoute>} />
            <Route path="/orders/:orderId/help" element={<PrivateRoute><HelpWithOrder /></PrivateRoute>} />
            <Route path="/profile" element={<PrivateRoute><Profile /></PrivateRoute>} />
            <Route path="/orders" element={<PrivateRoute><Orders /></PrivateRoute>} />
            <Route path="/favorites" element={<PrivateRoute><Favorites /></PrivateRoute>} />
            <Route path="/addresses" element={<PrivateRoute><AddressBook /></PrivateRoute>} />
            <Route path="/coupons" element={<PrivateRoute><Coupons /></PrivateRoute>} />
            {/* /settings merged into /profile — render the redirect (it forwards
                ?section=preferences). Unguarded so the redirect runs; /profile's
                PrivateRoute then enforces auth. */}
            <Route path="/settings" element={<Settings />} />
            <Route path="/my-subscriptions" element={<PrivateRoute><MySubscriptions /></PrivateRoute>} />
            <Route path="/admin" element={<AdminRoute><AdminDashboard /></AdminRoute>} />
            <Route path="/investor" element={<InvestorRoute><InvestorDashboard /></InvestorRoute>} />
            {/* Staff portal (Business Rule #41) — first nested-route section.
                StaffLayout handles auth redirect + the not-activated gate itself. */}
            <Route path="/staff" element={<StaffLayout />}>
              <Route index element={<StaffHome />} />
              <Route path="checklists" element={<StaffChecklists />} />
              <Route path="checklists/:runId" element={<StaffChecklistRun />} />
              <Route path="review" element={<StaffReviewQueue />} />
              <Route path="sops" element={<StaffSops />} />
              <Route path="sops/:sopId" element={<StaffSopDetail />} />
              <Route path="launches" element={<StaffLaunches />} />
              <Route path="launches/series/:seriesId" element={<StaffLaunchSeries />} />
              <Route path="launches/series/:seriesId/test" element={<StaffSeriesTest />} />
              <Route path="exams" element={<StaffExams />} />
              <Route path="exams/:examId" element={<StaffExamTake />} />
              <Route path="incidents" element={<StaffIncidents />} />
              <Route path="planner" element={<StaffPlanner />} />
              <Route path="planner/:taskId" element={<StaffPlanner />} />
              <Route path="documents" element={<StaffDocuments />} />
              <Route path="cups/:branchId" element={<StaffCupCounts />} />
              <Route path="warehouse-count" element={<StaffWarehouseCount />} />
              <Route path="recipes/:parentSku" element={<StaffRecipeDetail />} />
            </Route>
            <Route path="/kiosk-login" element={<KioskLogin />} />
            <Route path="/kiosk" element={<KioskScanner />} />
            <Route path="/offer-of-the-day" element={<Suspense fallback={<OffersSkeleton />}><OfferOfTheDay /></Suspense>} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/subscriptions" element={<Suspense fallback={<PlansSkeleton />}><Subscriptions /></Suspense>} />
            <Route path="/passes/:id" element={<PassDetail />} />
            <Route path="/pass-confirmed/:userSubId" element={<PrivateRoute><PassConfirmation /></PrivateRoute>} />
            <Route path="/contact" element={<ContactUs />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/account-deletion" element={<AccountDeletion />} />
            <Route path="/refunds" element={<RefundsAndCancellations />} />
            {/* Public tokenized visit-feedback survey (BR#46) — reached only
                via the WhatsApp link, never from app navigation. Unguarded;
                outer Suspense spinner covers the lazy load. */}
            <Route path="/f/:token" element={<VisitFeedback />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
  );

  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-[60vh]"><div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>}>
      {/* All /staff* paths share ONE motion key (Business Rule #41): the staff
          shell (header + tab bar + /staff/me gate) must never remount on
          internal navigation — StaffLayout owns its own lightweight content fade. */}
      {isNativeShell ? pageRoutes : (
        <AnimatePresence mode="wait" initial={false}>
          <motion.div
            key={routedLocation.pathname.startsWith('/staff') ? '/staff' : routedLocation.pathname}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
          >
            {pageRoutes}
          </motion.div>
        </AnimatePresence>
      )}

      {/* Sheet route — only rendered when backgroundLocation is set (in-app nav).
          Renders OVER whatever the primary <Routes> drew (typically Menu).
          OUTSIDE the page transition so the vaul drawer is the only motion.
          Its OWN Suspense with a null fallback: if the sheet chunk is still
          downloading (cold first tap) it bubbles here, not to the outer
          spinner — so Menu underneath stays visible instead of being replaced
          by a full-screen spinner while the drawer chunk loads. */}
      {backgroundLocation && (
        <Suspense fallback={null}>
          <Routes>
            <Route path="/menu/item/:itemId" element={<ProductDetailSheet />} />
          </Routes>
        </Suspense>
      )}
    </Suspense>
  );
}

function AppWithBottomNav() {
  const location = useLocation();
  // Festive theme (services/seasonal_theme.py decides the window): one body class
  // the CSS keys off, plus `festive-off` on Cart / Checkout / Login / staff / admin
  // routes. Layout effect so a route change never paints one frame unthemed.
  const { seasonal_theme: seasonalTheme } = useFeatureFlags();
  useLayoutEffect(() => {
    applySeasonalTheme(seasonalTheme, location.pathname);
    if (seasonalTheme) rememberSeasonalTheme(seasonalTheme);
  }, [seasonalTheme, location.pathname]);
  // When a ProductDetail sheet is open (backgroundLocation set), the bottom nav
  // follows the BACKGROUND page (e.g. /menu) so it stays mounted UNDER the drawer
  // the whole time — closing the sheet reveals a nav that was always there (the
  // vaul portal renders over it), instead of the nav re-mounting / "loading back".
  // Cold-open /menu/item (no backgroundLocation) is the full-page ProductDetail and
  // still correctly hides the nav.
  const navPathname = (location.state?.backgroundLocation || location).pathname;
  const hideBottomNav = HIDE_NAV_PATHS.some(p => navPathname.startsWith(p));
  // The cart drawer keeps the raw-location gate so opening the item sheet doesn't
  // surface the cart preview over it (behaviour unchanged).
  const hideCartDrawer = HIDE_NAV_PATHS.some(p => location.pathname.startsWith(p));

  // Platform-appropriate toasts (docs/NATIVE_POLISH_PLAN.md §4.6): top banner on
  // iOS (offset below the notch/Dynamic Island), bottom snackbar on Android
  // (lifted above the BottomNav + home indicator). Web PWA is unchanged —
  // top-center with Sonner's default offset — because isNative gates everything.
  // env(safe-area-inset-*) is baked into the offsets so it's correct per device.
  const cap = typeof window !== 'undefined' ? window.Capacitor : undefined;
  const isNative = !!cap?.isNativePlatform?.();
  const platform = cap?.getPlatform?.();
  const toastPosition = isNative && platform === 'android' ? 'bottom-center' : 'top-center';
  const toastOffset = !isNative
    ? undefined
    : platform === 'android'
      ? { bottom: hideBottomNav ? 'calc(env(safe-area-inset-bottom) + 16px)' : 'calc(env(safe-area-inset-bottom) + 80px)' }
      : { top: 'calc(env(safe-area-inset-top) + 12px)' };

  // Register push notifications when running inside the Capacitor native shell
  usePushNotifications();

  // Native app-open ping (Business Rule #45) — stamps `app_first_seen_at` and
  // grants the welcome coffee to a qualifying new install. Mounted here rather
  // than in App() because it needs the resolved auth context. No-op on web.
  useAppSessionPing();

  // iOS left-edge swipe-back gesture (no-op off iOS; Android uses its system
  // back, which already pops history + animates via the transition effect).
  useIosEdgeSwipeBack();

  // Native: warm the bottom-nav route chunks on idle so the FIRST visit to a tab
  // doesn't flash the white Suspense fallback while its code chunk downloads.
  // webpack dedupes these against the React.lazy imports above.
  useEffect(() => {
    if (!isNative) return undefined;
    const warm = () => {
      // .preload() resolves the module AND flips the synchronous-render path on, so
      // the first tab switch renders the real page (no Suspense fallback for the VT
      // to capture). Plain import() warmed only the chunk and left the flicker.
      Menu.preload();
      OfferOfTheDay.preload();
      Subscriptions.preload();
      Cart.preload();
      ProductDetail.preload();
      ProductDetailSheet.preload();
    };
    const ric = typeof window !== 'undefined' ? window.requestIdleCallback : null;
    const id = ric ? ric(warm, { timeout: 2500 }) : setTimeout(warm, 1500);
    return () => {
      if (ric && window.cancelIdleCallback) window.cancelIdleCallback(id);
      else clearTimeout(id);
    };
  }, [isNative]);
  // Note: useNativeShellInit() is now called at the App() root level so
  // permission prompts fire on the very first render, not after navigation.

  // Dismiss splash for non-landing pages (landing handles its own dismissal)
  useEffect(() => {
    if (location.pathname !== '/') {
      window.dispatchEvent(new Event('app-ready'));
    }
  }, [location.pathname]);

  // Register service worker — but NOT inside Capacitor native wrappers.
  // Native WebView + a site-level SW cause navigation + cache conflicts
  // (the app can get stuck on the splash or serve stale assets).
  useEffect(() => {
    const isCapacitor = typeof window !== 'undefined' && (
      window.Capacitor?.isNativePlatform?.() ||
      /capacitor:\/\//i.test(window.location.protocol || '')
    );
    if (isCapacitor) {
      // Proactively unregister any previously-cached SW from a prior web visit
      if ('serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistrations?.().then(regs => regs.forEach(r => r.unregister())).catch(() => {});
      }
      return;
    }
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then((reg) => {
          setInterval(() => reg.update(), 5 * 60 * 1000);
        })
        .catch(() => {});
    }
  }, []);

  return (
    <>
      {/* On iOS the home indicator adds ~34px to the fixed BottomNav height.
          env(safe-area-inset-bottom) is 0 on Android/web so this is a no-op there. */}
      <div style={hideBottomNav ? undefined : { paddingBottom: 'env(safe-area-inset-bottom)' }}>
        <AppRoutes />
      </div>
      {!hideBottomNav && <BottomNav />}
      {!hideCartDrawer && <CartDrawer />}
      {/* The CA agent's floating chat — renders only for an admin account (Business Rule #55).
          Drops to the screen edge where there's no bottom nav (e.g. /admin). */}
      <CaAgentChat navHidden={hideBottomNav} />
      {/* Web-only "Get the app" banner (self-gates to mobile web; null inside the
          native app + on desktop). Suppressed where the bottom nav is hidden
          (conversion/auth pages) and on Cart, which owns the same bottom strip. */}
      <AppDownloadBanner suppressed={hideBottomNav || location.pathname === '/cart'} />
      <Toaster
        position={toastPosition}
        offset={toastOffset}
        mobileOffset={toastOffset}
        duration={3000}
        closeButton
      />
    </>
  );
}

function App() {
  // Fire native shell init (status bar, splash hide, location & notification
  // permissions) at the OUTERMOST level so it runs the moment React mounts —
  // before AuthProvider/CartProvider/Routes resolve. This is what makes the
  // OS permission prompts appear within the first second of app launch
  // rather than after the home page has fully rendered.
  useNativeShellInit();

  // App-engagement tracking: fires `app_open` on first mount and on every
  // visibilitychange → visible, plus a 60s `session_heartbeat` while the
  // document is visible. Powers DAU/WAU/MAU + avg session time in the admin
  // analytics dashboard. Fire-and-forget — failures never affect UX.
  useEffect(() => {
    trackAppOpen();
    startSessionHeartbeat();
    // Microsoft Clarity heatmaps / session recordings — web + both native WebViews.
    // No-op off production (see utils/clarity.js); the async tag never blocks render.
    initClarity();
  }, []);

  return (
    <BrowserRouter>
      <AuthProvider>
        <CartProvider>
          <VegModeProvider>
            <AppWithBottomNav />
          </VegModeProvider>
        </CartProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;